import React, { useEffect, useRef, useState } from "react";
import API from "../../utils/API";
import { storage } from "../../config/Fire";
import { FEATURES } from "../../config/features";
import { formatDate } from "../../data/format";
import { Button, FileIcon, useToast } from "../../ui";

/**
 * Proposal-PDF slot (D6). Reads a quote's documents and, when a
 * docKind:"proposal" doc with a real file exists, offers a download. Staff
 * (canUpload) additionally get upload-when-none and replace-when-exists via the
 * existing POST /api/documents/quote/proposal/:id — the browser uploads the
 * binary to Firebase Storage first, then the metadata POST (the ruled v1 path,
 * same as the refer form's dec pages); Session I stamps docKind:"proposal" on
 * that route. Referral tier passes canUpload=false → download-only, and renders
 * NOTHING when no proposal exists (E's no-dead-affordance law). Shared by the
 * staff hub Quotes tab now and the referral case drawer (Unit 4). Gated behind
 * FEATURES.proposalDocs.
 */

/**
 * Honest upload-failure copy (post-gate polish). The upload is two hops: a
 * Firebase Storage `put` (errors carry a `storage/*` code) then the metadata
 * POST (an axios error with response.status). Deterministic failures —
 * permission, quota, billing, an expired session — must NOT say "try again",
 * because retrying can't fix them; genuine transients (network, 5xx, rate
 * limit, retry-limit) keep the retry advice. Cloud billing is live on prod now,
 * so the quota/402 arms are future headroom rather than today's path.
 */
export function uploadErrorMessage(err) {
  const code = err && err.code; // Firebase Storage error, e.g. "storage/quota-exceeded"
  if (code === "storage/unauthorized" || code === "storage/unauthenticated") {
    return "You're not allowed to upload here — check you're still signed in, or contact your admin.";
  }
  if (code === "storage/quota-exceeded") {
    return "File storage is over its limit — retrying won't help. Let Evan know so it can be raised.";
  }
  if (code === "storage/canceled") {
    return "Upload canceled.";
  }
  const status = err && err.response && err.response.status;
  if ((err && err.isRateLimited) || status === 429) {
    return "The server's busy right now — give it a moment and try again.";
  }
  if (status === 402) {
    return "Uploads are paused for a billing issue — retrying won't help. Let Evan know.";
  }
  if (status === 403) {
    return "You don't have permission to add a proposal to this quote.";
  }
  if (status === 413) {
    return "That file is too large to upload.";
  }
  return "Upload didn't finish — try again."; // genuine transient (network / 5xx / retry-limit)
}

function ProposalDownloadSlot({ quoteId, canUpload = false }) {
  const toast = useToast();
  const fileRef = useRef(null);
  const [proposal, setProposal] = useState(null);
  const [ready, setReady] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!FEATURES.proposalDocs || !quoteId) {
      setReady(true);
      return undefined;
    }
    let cancelled = false;
    API.getDocumentsByOwner("Quote", quoteId)
      .then((res) => {
        if (cancelled) {
          return;
        }
        const docs = Array.isArray(res.data) ? res.data : [];
        // Only a proposal with an actual file — imported metadata-only docs
        // (no binary) must not surface a dead download.
        setProposal(docs.find((d) => d.docKind === "proposal" && d.document) || null);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) {
          setReady(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [quoteId, reloadKey]);

  const onPick = (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) {
      return;
    }
    setUploading(true);
    storage
      .ref(`proposals/${Date.now()}-${file.name}`)
      .put(file)
      .then((snap) => snap.ref.getDownloadURL())
      .then((url) =>
        API.createDocumentsForQuoteProposal(quoteId, {
          docName: file.name,
          document: url,
          type: "Proposal"
        })
      )
      .then(() => {
        toast.success("Proposal added.");
        setReloadKey((k) => k + 1);
      })
      .catch((err) => toast.error(uploadErrorMessage(err)))
      .finally(() => setUploading(false));
  };

  if (!FEATURES.proposalDocs || !ready) {
    return null;
  }

  if (proposal) {
    return (
      <span className="feat-proposal">
        <a
          className="feat-proposal__dl"
          href={proposal.document}
          target="_blank"
          rel="noopener noreferrer"
        >
          <FileIcon size={14} /> Download the proposal
        </a>
        <span className="feat-proposal__meta">PDF · added {formatDate(proposal.created_at)}</span>
        {canUpload ? (
          <>
            <Button
              size="sm"
              variant="ghost"
              loading={uploading}
              onClick={() => fileRef.current && fileRef.current.click()}
            >
              Replace
            </Button>
            <input
              ref={fileRef}
              type="file"
              accept=".pdf,application/pdf"
              hidden
              onChange={onPick}
            />
          </>
        ) : null}
      </span>
    );
  }

  if (!canUpload) {
    return null; // referral: absent, not empty
  }

  return (
    <>
      <Button
        size="sm"
        variant="ghost"
        loading={uploading}
        onClick={() => fileRef.current && fileRef.current.click()}
      >
        Add a proposal PDF
      </Button>
      <input ref={fileRef} type="file" accept=".pdf,application/pdf" hidden onChange={onPick} />
    </>
  );
}

export default ProposalDownloadSlot;
